import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Client, Department } from '../objects/client';
import { LocalStorageService } from './local-storage.service';
import * as initHelpHero from 'helphero';
import { blank, fallback, filled, OptionalValue, safely_parse_json } from '../shared/utils/common';

/**
 * The clients list stored in local storage has this format:
 * <client id>: <client object>
 * We can use this type when getting the list of clients.
 *
 * @type {{ [key: string]: Client }}
 */
export type ClientListObject = { [key: string]: Client };

@Injectable({
  providedIn: 'root'
})
export class ClientStoreService {

  /**
   * The active client's data. We'll use this to publish client changes
   * to the subscribers.
   *
   * @type {BehaviorSubject<Client>}
   */
  private activeClient: BehaviorSubject<Client> = new BehaviorSubject<Client>(null);

  /**
   * Public observable for accessing the activeClient BehaviorSubject.
   *
   * @type {Observable<Client>}
   */
  public activeClient$: Observable<Client> = this.activeClient.asObservable();

  /**
   * An observable for any changes to the active client. We will
   * monitor "watch" any changes to the active client so that we may
   * be able to emit events for when it happens.
   *
   * @type Observable<Client>
   */
  public readonly whenActiveClientIsSet$: Observable<Client> = this.activeClient$
    .pipe(
      filter(client => client !== null)
    );

  /**
   * This is the exact opposite of whenActiveClientIsSet$. This will only
   * emit values if there is no active client.
   *
   * @type Observable<Client>
   */
  public readonly whenActiveClientIsRemoved$: Observable<Client> = this.activeClient.asObservable()
    .pipe(
      filter(activeClient => activeClient === null)
    );

  constructor(
    private readonly _store: LocalStorageService,
  ) {
    const stored = this._store.getJsonItem('current_client');

    if (filled(stored)) {
      this.setActiveClient(stored);
    }
  }

  /**
   * Returns the current client's data.
   *
   * @returns {OptionalValue<Client>}
   */
  getActiveClient(): OptionalValue<Client> {
    let client = this.activeClient.getValue();

    /// init from local store if current state didn't contain it
    if (blank(client)) {
      const stored = this._store.getJsonItem('current_client');

      if (filled(stored)) {
        client = stored;
        this.activeClient.next(client);
      }
    }

    if (filled(client)) {
      client.address = fallback(safely_parse_json(client.address), {
        to: () => client.address,
      });
    }

    return client;
  }

  /**
   * Stores the active client in local storage for later use.
   *
   * @param {Client} activeClient
   *
   * @returns {void}
   */
  setActiveClient(activeClient: Client){
    // Make sure we remove the last accessed dashboard when changing clients.
    this._store.removeItem('last_dashboard_accessed');
    this._store.replaceJsonItem('current_client', activeClient);
    this.setHelpHeroUser();
    this.activeClient.next(this._store.getJsonItem('current_client'));
  }

  /**
   * Stores the previous selected client in local storage for later use.
   *
   * @param {Client} activeClient
   *
   * @returns {void}
   */
  setPreviousClient(activeClient: Client){
    // Make sure we remove the last accessed dashboard when changing clients.
    this._store.replaceJsonItem('previous_client', activeClient);
  }

  /**
   * Function that set the user/role/name for HelpHero to identify.
   */
  setHelpHeroUser() {
    let objData = this._store.getJsonItem('current_client');
    let strId = this._store.getItem('user_id');
    let strName = this._store.getItem('user_name');

    initHelpHero.default('rZzjEVSgKoC').identify(strId, {
      name: strName,
      role: objData['level']
    });
  }

  /**
   * Removes the current client from local storage and emits
   * an event to all activeClient subscribers.
   *
   * @returns {void}
   */
  removeActiveClient() {
    this._store.removeItem('current_client');
    this.activeClient.next(null);
  }

  /**
   * Removes the previous client from local storage and emits
   * an event to all activeClient subscribers.
   *
   * @returns {void}
   */
  removePreviousClient() {
    this._store.removeItem('previous_client');
    this.activeClient.next(null);
  }

  /**
   * check if current admin is client
   *
   * @returns {boolean}
   */
  isAdmin(): boolean {
    return this.activeClient.getValue().level === UserAccessLevel.admin;
  }

  /**
   * Retrieved the event observable that will be triggered when client is set/selected
   *
   * NOTE: this will allow testability of the components/directives/etc that relies on this event which would allows
   * us to mock the property using the jasmine.createSpyObj helper.
   *
   * @returns {Observable<Client>}
   */
  getWhenClientIsSetEvent(): Observable<Client> {
    return this.whenActiveClientIsSet$;
  }

  /**
   * Check if department tracking enabled.
   *
   * @return {boolean}
   */
  isDepartmentTracking(): boolean {
    return this.activeClient.getValue().config.department_tracking || false;
  }

  /**
   * Check if department restriciton is enabled.
   *
   * @return {boolean}
   */
  isDepartmentRestricted(): boolean {
    return this.activeClient.getValue().is_department_restricted || false;
  }

  /**
   * Departments assigned to the user
   *
   * @return {Department[]}
   */
  departments(): Department[] {
    return this.activeClient.getValue().department_id || [];
  }
}

export enum UserAccessLevel {
  admin = 'admin',
  desktop = 'desktop',
  mobileFull = 'mobile-premium',
  mobileLite = 'mobile-lite',
}